// Call notes (D-111): every note owners and admins wrote when running a call's AI again, newest first, with the call
// (and its ID), where the mistake was, and what the AI said before and says now. The log we improve the AI from.
import { Link } from "react-router";
import { fmtDateTime } from "../lib/format";
import { layerLabel, noteCounts, readingLine } from "../lib/callNotes";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, Stat, useLoad } from "../app/ui";

export function CallNotes() {
  const { api, tz } = useMember();
  const { data, error, loading } = useLoad(() => api.callNotes(), [api]);
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const counts = noteCounts(data);

  return (
    <Page
      title="Call notes"
      sub="What you found wrong in the AI's reading of a call when you ran it again. Each run reads your note; this log shows where readings go wrong."
    >
      <ErrorNote error={error} />
      <div className="stack">
        <div className="grid cols-4">
          <Stat label="Notes" value={counts.all} hint="Written with Run AI again, on a call's page" />
          <Stat label="Transcript" value={counts.transcript} hint="The words or who said them" />
          <Stat label="Analysis" value={counts.analysis} hint="What the AI made of the words" />
          <Stat label="Both" value={counts.both} hint="The transcript and the analysis" />
        </div>
        <Card title="Notes" flush>
          {data.length === 0
            ? <Empty>No notes yet. On a call's page, press Run AI again and say what was wrong.</Empty>
            : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Written</th>
                      <th>Call</th>
                      <th>Where</th>
                      <th>Note</th>
                      <th>Before</th>
                      <th>Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((n) => (
                      <tr key={n.id}>
                        <td className="nowrap">
                          {fmtDateTime(n.created_at, tz)}
                          <div className="faint">{n.author ?? ""}</div>
                        </td>
                        <td>
                          <Link to={`/calls/${n.recording_id}`}>{n.call?.customer ?? "Call"}</Link>
                          <div className="faint">
                            {n.call ? fmtDateTime(n.call.started_at, tz) : ""}
                            {n.caller ? ` · ${n.caller}` : ""}
                          </div>
                          <div className="faint" title="Call ID">{n.call_id ?? n.recording_id}</div>
                        </td>
                        <td>
                          <span className={n.layer === "analysis" ? "badge brand" : "badge warm"}>
                            {layerLabel[n.layer]}
                          </span>
                        </td>
                        <td>{n.note}</td>
                        <td className="faint">{readingLine(n.before, tz)}</td>
                        <td>
                          {n.now
                            ? readingLine(n.now, tz)
                            : <span className="faint">{n.recording_status === "failed" ? "Failed" : "Being read"}
                            </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      </div>
    </Page>
  );
}
